import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Lock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BACKEND_URL } from '../lib/api';

const STATUS_LABELS = {
  pending_organiser_review: 'Pending Organiser Review',
  approved_by_organiser:    'Approved by Organiser',
  rejected_by_organiser:    'Rejected by Organiser',
  pending_admin_processing: 'Pending VinePass Processing',
  processing_payfast:       'Processing via PayFast',
  refunded_payfast:         'Refunded via PayFast',
  manual_eft_required:      'Manual EFT — banking details required',
  manual_eft_paid:          'EFT Paid',
  failed:                   'Failed',
  cancelled:                'Cancelled',
};

const CustomerProvideBankDetailsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const { refundId } = useParams();
  const navigate = useNavigate();
  const [refund, setRefund] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    account_holder_name: '',
    bank_name: '',
    account_number: '',
    branch_code: '',
    account_type: '',
  });
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(`/refund-requests/${refundId}/bank-details`)}`);
      return;
    }
    const fetchRefund = async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/refund-requests/${refundId}`, { withCredentials: true });
        setRefund(r.data.refund_request);
        // Pre-fill account holder with customer name if known
        setForm((f) => ({ ...f, account_holder_name: r.data.refund_request.customer_name || '' }));
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Unable to load this refund.');
      } finally {
        setLoading(false);
      }
    };
    fetchRefund();
  }, [refundId, user, authLoading, navigate]);

  if (loading || authLoading) {
    return <div className="min-h-screen bg-stone-50 pt-32 text-center text-stone-500">Loading…</div>;
  }
  if (!refund) {
    return (
      <div className="min-h-screen bg-stone-50 pt-32 px-6 max-w-xl mx-auto text-center">
        <AlertCircle className="w-10 h-10 mx-auto text-stone-400 mb-3" />
        <h1 className="font-serif text-2xl text-[#1F3D2B] mb-2">Refund request not found</h1>
        <p className="text-stone-600">This link may have expired or the request no longer exists.</p>
        <Button className="mt-6 bg-[#1F3D2B]" onClick={() => navigate('/my-tickets')}>Back to my tickets</Button>
      </div>
    );
  }

  const status = refund.status;
  const alreadySubmitted = !!refund.bank_details_submitted;
  const gateOpen = status === 'manual_eft_required';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error('Please confirm consent to use your banking details for this refund.');
      return;
    }
    const acc = form.account_number.replace(/\D/g, '');
    if (acc.length < 5 || acc.length > 20) {
      toast.error('Account number must be 5-20 digits.');
      return;
    }
    const branch = form.branch_code.replace(/\D/g, '');
    if (branch.length < 4 || branch.length > 8) {
      toast.error('Branch code must be 4-8 digits.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/refund-requests/${refundId}/bank-details`,
        {
          account_holder_name: form.account_holder_name.trim(),
          bank_name: form.bank_name.trim(),
          account_number: acc,
          branch_code: branch,
          account_type: form.account_type || undefined,
          consent_confirmed: true,
        },
        { withCredentials: true },
      );
      toast.success('Banking details submitted securely. VinePass will process your refund shortly.');
      // Re-fetch to show submitted state
      const r = await axios.get(`${BACKEND_URL}/api/refund-requests/${refundId}`, { withCredentials: true });
      setRefund(r.data.refund_request);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit banking details.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 pt-24 pb-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8">
          <Lock className="w-10 h-10 mx-auto text-[#1F3D2B] mb-3" />
          <h1 className="font-serif text-3xl sm:text-4xl text-[#1F3D2B]">Provide banking details</h1>
          <p className="text-sm text-stone-600 mt-2">for refund #{refundId.slice(0, 8).toUpperCase()} · {refund.event_title}</p>
        </div>

        {/* Status banner */}
        {gateOpen && !alreadySubmitted ? (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6" data-testid="refund-status-banner">
            <p className="text-sm text-orange-900">
              <strong>Action required:</strong> {STATUS_LABELS[status]}. Please submit your banking details below to receive your EFT refund.
            </p>
          </div>
        ) : alreadySubmitted ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="refund-bank-submitted-banner">
            <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
            <div className="text-sm text-emerald-900">
              <strong>Banking details submitted.</strong> We've recorded your account ending in <span className="font-mono">{refund.bank_details_submitted.account_ending}</span> at <strong>{refund.bank_details_submitted.bank_name}</strong>. VinePass will process the EFT within 5–7 business days.
            </div>
          </div>
        ) : (
          <div className="bg-stone-100 border border-stone-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-stone-700">
              <strong>Current status:</strong> {STATUS_LABELS[status]}. Banking details are not currently required — they're only accepted when VinePass admin has flagged your refund for manual EFT.
            </p>
          </div>
        )}

        {/* Trust banner */}
        <div className="bg-white border border-stone-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-[#1F3D2B] mt-0.5 shrink-0" />
          <div className="text-xs text-stone-600 leading-relaxed">
            <strong className="text-[#1F3D2B]">Banking details are only required where a refund cannot be processed through the original PayFast payment method.</strong> These details will be used only to process the approved refund. Your account number is encrypted at rest and is only visible to authorised VinePass admin staff with explicit confirmation — every reveal is audit-logged.
          </div>
        </div>

        {gateOpen && !alreadySubmitted && (
          <form className="bg-white border border-stone-200 rounded-lg p-6 space-y-4" onSubmit={handleSubmit} data-testid="customer-bank-form">
            <div>
              <Label htmlFor="holder">Account holder name *</Label>
              <Input
                id="holder" data-testid="bank-holder" required
                value={form.account_holder_name}
                onChange={(e) => setForm({ ...form, account_holder_name: e.target.value })}
                placeholder="As it appears on the account"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bank">Bank name *</Label>
                <Input
                  id="bank" data-testid="bank-name" required
                  value={form.bank_name}
                  onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                  placeholder="e.g. Standard Bank"
                />
              </div>
              <div>
                <Label htmlFor="type">Account type</Label>
                <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })}>
                  <SelectTrigger data-testid="bank-type"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="savings">Savings</SelectItem>
                    <SelectItem value="transmission">Transmission</SelectItem>
                    <SelectItem value="business">Business</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="branch">Branch code *</Label>
                <Input
                  id="branch" data-testid="bank-branch" required
                  value={form.branch_code}
                  onChange={(e) => setForm({ ...form, branch_code: e.target.value })}
                  placeholder="6 digits"
                  inputMode="numeric"
                />
              </div>
              <div>
                <Label htmlFor="acc">Account number *</Label>
                <Input
                  id="acc" data-testid="bank-account" required
                  value={form.account_number}
                  onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                  placeholder="No spaces"
                  inputMode="numeric"
                />
              </div>
            </div>

            <div className="flex items-start gap-3 pt-2">
              <Checkbox id="consent" data-testid="bank-consent" checked={consent} onCheckedChange={setConsent} className="mt-1" />
              <Label htmlFor="consent" className="text-sm text-stone-700 leading-relaxed cursor-pointer">
                I confirm that these banking details belong to me and may be used by VINEPASS (Pty) Ltd solely to process the approved refund for this order.
              </Label>
            </div>

            <Button
              type="submit" disabled={submitting || !consent}
              className="w-full bg-[#1F3D2B] hover:bg-[#1F3D2B]/90 text-white"
              data-testid="bank-submit-btn"
            >
              {submitting ? 'Submitting securely…' : 'Submit banking details'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default CustomerProvideBankDetailsPage;
